import type { ClassBuildGuide } from '../../types/classBuild'
import type { ClassId } from '../../types/progression'

/**
 * Mini guías por clase. Las distribuciones son ORIENTACIONES para principiantes,
 * no builds óptimas comprobadas en Vanguard. Base de referencia: guías de clase de Webzen (sin renovaciones posteriores a Season 6).
 */
export const classBuilds: ClassBuildGuide[] = [
  {
    classId: 'dk',
    intro: 'Guerrero de combate cuerpo a cuerpo, con ataques físicos a un objetivo y en área. Para quien quiere luchar en primera línea.',
    styles: [
      {
        id: 'fisico',
        name: 'Físico para levelear',
        summary: 'Daño físico para cazar, sin descuidar la vida.',
        aliases: ['bk de fuerza', 'dk de fuerza', 'bk fuerza agilidad', 'bk para levelear'],
        stats: { str: { p: 'principal' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia' }, ene: { p: 'requisitos', note: 'Revisa su relación con los buffs' } },
      },
      {
        id: 'equilibrado',
        name: 'Equilibrado',
        summary: 'Daño y resistencia a partes más parejas.',
        aliases: ['bk equilibrado', 'dk equilibrado'],
        stats: { str: { p: 'principal' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia', note: 'Mayor inversión defensiva' }, ene: { p: 'requisitos', note: 'Según tus habilidades' } },
      },
      {
        id: 'resistente',
        name: 'Resistente / PvP',
        summary: 'Prioriza aguantar sin quedarte sin daño útil.',
        aliases: ['bk de vida', 'bk pvp', 'dk pvp', 'bk vitalidad', 'bk resistente'],
        stats: { str: { p: 'complementaria', note: 'Mantén un daño útil' }, agi: { p: 'complementaria' }, vit: { p: 'principal', note: 'Prioridad defensiva' }, ene: { p: 'requisitos', note: 'Revisa la fórmula de los buffs' } },
        caution: 'Es una orientación de juego, no la mejor build PvP de Vanguard: no hay pruebas que lo respalden.',
      },
    ],
    startTips: [
      'Consigue un arma compatible y una habilidad útil.',
      'Desarrolla fuerza y agilidad sin dejar que la falta de vida te impida quedarte en el spot.',
    ],
    skills: [{ name: 'Twisting Slash' }, { name: 'Death Stab' }, { name: 'Greater Fortitude' }],
    mistakes: ['Creer que la energía no aporta nada después de aprender los poderes. Su relación con los buffs debe revisarse antes de recomendar un mínimo definitivo.'],
    cautions: ['No se publica un mínimo de energía hasta conocer la fórmula de los buffs en Vanguard.'],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/105',
    related: [
      { label: 'Misión de Marlon', to: '/guia/evoluciones?clase=dk#marlon' },
      { label: 'Desbloquear el combo', to: '/guia/evoluciones?clase=dk#combo' },
    ],
  },
  {
    classId: 'dw',
    intro: 'Especialista en magia, ataques a distancia y habilidades que alcanzan a varios enemigos. Su posición es clave para sobrevivir.',
    styles: [
      {
        id: 'leveleo',
        name: 'Mago para levelear',
        summary: 'Energía para cazar rápido, con la vida justa para el spot.',
        aliases: ['mago de energia', 'sm de energia', 'dw de energia', 'mago para levelear'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia' }, ene: { p: 'principal' } },
      },
      {
        id: 'equilibrado',
        name: 'Mago equilibrado',
        summary: 'Algo menos de daño a cambio de más resistencia.',
        aliases: ['mago equilibrado', 'sm equilibrado'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia', note: 'Mayor inversión defensiva' }, ene: { p: 'principal' } },
      },
      {
        id: 'pvp',
        name: 'PvP / movilidad',
        summary: 'Resistir y reposicionarse sin perder el daño útil.',
        aliases: ['mago pvp', 'sm pvp', 'dw pvp'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria', note: 'Ajústala a tu equipo y habilidades' }, vit: { p: 'supervivencia', note: 'Importante para resistir' }, ene: { p: 'complementaria', note: 'Mantén un daño útil' } },
      },
    ],
    startTips: [
      'Prioriza aprender una habilidad de ataque adecuada para el spot y poder usarla sin parar.',
      'Cumple los requisitos del bastón y del equipo antes de gastar más puntos en fuerza.',
    ],
    skills: [{ name: 'Evil Spirit' }, { name: 'Aqua Beam' }, { name: 'Ice' }, { name: 'Teleport', note: 'Ayuda a reposicionarte y esquivar ataques.' }],
    mistakes: [
      'Subir fuerza como si fueras un guerrero.',
      'Elegir un spot que te mata rápido.',
      'Desarrollar daño sin poder sostener el gasto de maná.',
    ],
    cautions: ['No se fijan límites de agilidad o velocidad ni requisitos exactos sin los datos de Vanguard.'],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/6',
  },
  {
    classId: 'elf',
    intro: 'Puede orientarse al combate con arco, al apoyo del grupo o combinar ambos. Sus buffs de ataque y defensa dependen de la energía.',
    styles: [
      {
        id: 'agilidad',
        name: 'Elfa de agilidad (arquera)',
        summary: 'Daño propio a distancia.',
        aliases: ['elfa de agilidad', 'agility elf', 'ae', 'elfa agi', 'elfa arquera', 'arquera'],
        stats: { str: { p: 'requisitos', note: 'Del arco y el equipo' }, agi: { p: 'principal' }, vit: { p: 'supervivencia' }, ene: { p: 'requisitos', note: 'Y apoyo básico' } },
      },
      {
        id: 'energia',
        name: 'Elfa de energía (bufeadora)',
        summary: 'Fortalece y cura al grupo.',
        aliases: ['elfa de energia', 'elfa bufeadora', 'bufeadora', 'energy elf', 'ee', 'elfa soporte', 'elfa de apoyo', 'build de elfa de energia'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'requisitos', note: 'Para el equipo y la supervivencia' }, vit: { p: 'supervivencia', note: 'Importante para mantenerse viva' }, ene: { p: 'principal' } },
        caution: 'Conviene planificar su progreso con una party: subiendo sola no tendrá el mismo ritmo que una arquera.',
      },
      {
        id: 'hibrida',
        name: 'Elfa híbrida',
        summary: 'Combina daño y apoyo.',
        aliases: ['elfa hibrida', 'elfa mixta'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'principal', note: 'Compartida con energía' }, vit: { p: 'supervivencia' }, ene: { p: 'principal', note: 'Compartida con agilidad' } },
        caution: 'Dividir puntos puede dejarte sin especialización mientras tienes pocos.',
      },
    ],
    startTips: [
      'Decide pronto si serás arquera, bufeadora o híbrida.',
      'Con pocos puntos, especializarte rinde más que repartir.',
    ],
    skills: [{ name: 'Triple Shot' }, { name: 'Penetration' }, { name: 'Ice Arrow' }, { name: 'Greater Damage' }, { name: 'Greater Defense' }, { name: 'Heal' }],
    mistakes: ['Esperar buffs fuertes subiendo solo agilidad, o buen daño de arco subiendo solo energía.'],
    cautions: [],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/283',
  },
  {
    classId: 'mg',
    intro: 'Combina recursos de guerrero y de mago. Puede especializarse en combate físico, en magia o en un desarrollo híbrido.',
    styles: [
      {
        id: 'fisico',
        name: 'MG físico',
        summary: 'Fuerza y agilidad alrededor de un arma y habilidades físicas.',
        aliases: ['mg de fuerza', 'mg fuerza', 'mg fisico', 'mg str'],
        stats: { str: { p: 'principal' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia' }, ene: { p: 'requisitos', note: 'De las habilidades' } },
      },
      {
        id: 'magico',
        name: 'MG mágico',
        summary: 'Energía con agilidad de apoyo. Sigue necesitando equipo y resistencia.',
        aliases: ['mg de energia', 'mg energia', 'mg magico', 'mg ene'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia' }, ene: { p: 'principal' } },
      },
      {
        id: 'hibrido',
        name: 'MG híbrido',
        summary: 'Usa ataques físicos y mágicos. No es repartir todo por igual: elige primero tu ataque principal.',
        aliases: ['mg hibrido', 'mg mixto'],
        stats: { str: { p: 'principal', note: 'Inversión ofensiva física' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia' }, ene: { p: 'principal', note: 'Inversión ofensiva mágica' } },
      },
    ],
    startTips: ['Elige qué ataque será tu herramienta principal y construye alrededor de él.'],
    skills: [{ name: 'Power Slash' }, { name: 'Fire Slash' }],
    mistakes: ['Querer usar todas las armas y habilidades desde el principio y terminar sin destacar en ninguna.'],
    cautions: [],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/243',
  },
  {
    classId: 'dl',
    intro: 'Combatiente con habilidades ofensivas, apoyo para la party y mascotas propias: Dark Raven y Dark Horse. Es la única clase con Comando.',
    styles: [
      {
        id: 'dano',
        name: 'Daño del personaje',
        summary: 'Tus propios ataques son la fuente principal de daño.',
        aliases: ['dl de fuerza', 'dl fuerza', 'dl de dano'],
        stats: {
          str: { p: 'principal', note: 'Base física' },
          agi: { p: 'complementaria' },
          vit: { p: 'supervivencia' },
          ene: { p: 'requisitos', note: 'Según tus habilidades' },
          cmd: { p: 'requisitos', note: 'Según tus mascotas' },
        },
      },
      {
        id: 'cuervo',
        name: 'Orientado al Dark Raven',
        summary: 'Tu progreso depende sobre todo del cuervo.',
        aliases: ['dl de cuervo', 'dl cuervo', 'dl raven', 'dl de comando', 'dl comando'],
        stats: {
          str: { p: 'requisitos' },
          agi: { p: 'requisitos' },
          vit: { p: 'supervivencia' },
          ene: { p: 'requisitos' },
          cmd: { p: 'principal', note: 'A evaluar con la fórmula del cuervo' },
        },
        caution: 'No hay una cifra universal de Comando: la fórmula del Dark Raven aún no está comprobada en Vanguard.',
      },
      {
        id: 'apoyo',
        name: 'Equilibrado / apoyo',
        summary: 'Daño útil, resistencia, buffs y mascota a la vez.',
        aliases: ['dl de apoyo', 'dl equilibrado', 'dl soporte'],
        stats: {
          str: { p: 'complementaria', note: 'Daño útil' },
          agi: { p: 'complementaria' },
          vit: { p: 'supervivencia' },
          ene: { p: 'requisitos', note: 'De tus buffs' },
          cmd: { p: 'requisitos', note: 'De tu mascota' },
        },
      },
    ],
    startTips: [
      'Decide si tu progreso dependerá de tus ataques o de una mascota.',
      'Revisa cómo se consiguen el Dark Raven y el Dark Horse antes de invertir en Comando.',
    ],
    skills: [{ name: 'Fire Burst' }, { name: 'Fire Scream' }, { name: 'Increase Critical Damage' }, { name: 'Summon' }, { name: 'Earthquake' }],
    mistakes: [
      'Subir Comando sin tener claro qué mascota vas a usar.',
      'Asumir que el caballo y el cuervo funcionan con la misma fórmula.',
    ],
    cautions: ['No se publica una cifra universal de Comando ni una distribución definitiva para mascotas hasta comprobar sus fórmulas en Vanguard.'],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/347',
    related: [{ label: 'Dark Horse y Dark Raven', to: '/guia/combinaciones' }],
  },
  {
    classId: 'sum',
    intro: 'Clase mágica con daño, debilitamientos y herramientas para cambiar el rumbo del combate.',
    styles: [
      {
        id: 'leveleo',
        name: 'Magia para levelear',
        summary: 'Energía para cazar, con la vida justa para el spot.',
        aliases: ['summoner de energia', 'sum de energia', 'como subir summoner', 'summoner para levelear'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia' }, ene: { p: 'principal' } },
      },
      {
        id: 'equilibrada',
        name: 'Equilibrada',
        summary: 'Algo menos de daño a cambio de más resistencia.',
        aliases: ['summoner equilibrada'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria' }, vit: { p: 'supervivencia', note: 'Mayor inversión defensiva' }, ene: { p: 'principal' } },
      },
      {
        id: 'apoyo',
        name: 'Apoyo / debilitamientos',
        summary: 'Mantenerse viva para seguir debilitando enemigos.',
        aliases: ['summoner de apoyo', 'summoner soporte', 'summoner debuff'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'complementaria', note: 'Según equipo y ritmo de combate' }, vit: { p: 'supervivencia', note: 'Importante para resistir' }, ene: { p: 'complementaria', note: 'Según daño y habilidades' } },
      },
    ],
    startTips: [
      'Usa equipo y poderes compatibles con Summoner: no todos los bastones ni hechizos del Dark Wizard le sirven.',
      'Aprende a distinguir cuándo te falta daño y cuándo te falta aguante para seguir debilitando.',
    ],
    skills: [{ name: 'Drain Life' }, { name: 'Chain Lightning' }, { name: 'Damage Reflection' }, { name: 'Sleep' }, { name: 'Weakness' }, { name: 'Innovation' }],
    mistakes: [
      'Subir fuerza como si fuera una clase física.',
      'Confundir sus libros con los de otras clases.',
      'Asumir que los debilitamientos afectan igual a todos los enemigos.',
    ],
    cautions: ['No se incorporan las renovaciones de Summoner de temporadas posteriores.'],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/90',
  },
  {
    classId: 'rf',
    intro: 'Luchador cuerpo a cuerpo con ataques y buffs. Elige tu habilidad principal antes de decidir cómo repartir.',
    styles: [
      {
        id: 'fisico',
        name: 'Físico inicial',
        summary: 'Fuerza como punto de partida.',
        aliases: ['rf de fuerza', 'rf fuerza'],
        stats: { str: { p: 'principal', note: 'Punto de partida' }, agi: { p: 'complementaria', note: 'Ajústala' }, vit: { p: 'supervivencia', note: 'Ajústala' }, ene: { p: 'requisitos' } },
      },
      {
        id: 'energia',
        name: 'Energía / área y buffs',
        summary: 'Energía como prioridad, sin perder requisitos ni aguante.',
        aliases: ['rf de energia', 'rf energia'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'requisitos' }, vit: { p: 'supervivencia' }, ene: { p: 'principal' } },
      },
      {
        id: 'vitalidad',
        name: 'Vitalidad / resistencia',
        summary: 'Más vida. Comprueba si tu habilidad gana también daño con ella.',
        aliases: ['rf de vida', 'rf vida', 'rf de vitalidad', 'rf vit'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'requisitos' }, vit: { p: 'principal', note: 'Comprueba si tu habilidad también gana daño' }, ene: { p: 'requisitos' } },
        caution: 'No está confirmado que la vitalidad aumente todos sus ataques.',
      },
      {
        id: 'agilidad',
        name: 'Agilidad / habilidad específica',
        summary: 'Especialización ligada a una habilidad concreta.',
        aliases: ['rf de agilidad', 'rf agi'],
        stats: { str: { p: 'requisitos' }, agi: { p: 'confirmar', note: 'Solo si se confirma cómo escala esa habilidad' }, vit: { p: 'supervivencia' }, ene: { p: 'requisitos' } },
        caution: 'Solo se recomendará como especialización cuando se confirme cómo escala esa habilidad en Vanguard.',
      },
    ],
    startTips: [
      'Elige la habilidad principal antes de repartir.',
      'La guía de Webzen distingue fuerza para cazar y energía para otras formas de combate y apoyo. Eso no permite usar la misma distribución con todas sus habilidades.',
    ],
    skills: [{ name: 'Chain Drive' }, { name: 'Dark Side' }, { name: 'Dragon Lore' }],
    mistakes: ['Copiar una build de RF de otro servidor sin comprobar sus fórmulas.'],
    cautions: ['No se afirma que vitalidad o agilidad aumenten todos sus ataques.'],
    officialUrl: 'https://muonline.webzen.com/en/gameinfo/guide/detail/87',
  },
]

export const getClassBuild = (id: ClassId | undefined) => classBuilds.find((b) => b.classId === id)

export const CLASSES_PATH = '/guia/clases'
export const classBuildPath = (id: ClassId, style?: string, hash?: string) =>
  `${CLASSES_PATH}?clase=${id}${style ? `&estilo=${style}` : ''}${hash ? `#${hash}` : ''}`
